/**
 * The gauge's arc, shared by the gauge and its loading outline so that the two always draw the same sweep. Pure.
 *
 * Angles are screen angles: 0 degrees points right (3 o'clock), and they grow clockwise, as SVG's y axis points down.
 */

/** Where the sweep starts and ends, in degrees: from the lower left, over the top, to the lower right. */
export const GAUGE_START = 150;
export const GAUGE_END = 390;

/** How far a mark reaches from the arc's centre line: a 14-px band's round cap, or the needle dot with its ring. */
export const GAUGE_REACH = 7;
/** The font size of the min and max labels. */
export const GAUGE_LABEL_SIZE = 9;
/** Room under the labels' baseline. */
const LABEL_DESCENT = 3;

/** The point at `deg` on a circle. */
export function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** The SVG path of the arc from `a0` to `a1`, on a circle of radius `r`. */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p0 = polar(cx, cy, r, a0);
  const p1 = polar(cx, cy, r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
}

/**
 * Where a gauge of `size` draws. The circle's bottom is open, so the height stops at what stands lowest: the labels
 * under the arc's ends. The labels start at the ends' outer edges and grow inward, so a long one stays in the picture.
 */
export function gaugeLayout(size: number) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 10;
  const start = polar(cx, cy, r, GAUGE_START);
  const end = polar(cx, cy, r, GAUGE_END);
  const labelY = Math.max(start.y, end.y) + GAUGE_REACH + GAUGE_LABEL_SIZE;
  return {
    cx,
    cy,
    r,
    labelY,
    minLabelX: start.x - GAUGE_REACH,
    maxLabelX: end.x + GAUGE_REACH,
    height: Math.ceil(labelY + LABEL_DESCENT),
  };
}
